import React, { useEffect, useRef, useState } from 'react';
import { PlusIcon, Squares2X2Icon, TrashIcon, PencilSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Block } from '../types';
import { BlockCalculationService } from '../services/BlockCalculationService';
import {
  LotBudgetInfo,
  formatDeltaPct,
  formatDeltaAmount,
  severityTextClass,
} from '../utils/lotBudget';

interface LotTabsProps {
  lots: Block[];
  activeLotId: string | null;
  onSelectLot: (id: string | null) => void;
  showRecap: boolean;
  onAddLot: () => void;
  onRenameLot: (id: string, newName: string) => void;
  onDeleteLot: (id: string) => void;
  lotBudgets?: Map<string, LotBudgetInfo>;
  onUpdateBudgetInitial?: (lotId: string, value: number | undefined) => void;
}

const LotTabs: React.FC<LotTabsProps> = ({
  lots,
  activeLotId,
  onSelectLot,
  showRecap,
  onAddLot,
  onRenameLot,
  onDeleteLot,
  lotBudgets,
  onUpdateBudgetInitial,
}) => {
  const [editingLotId, setEditingLotId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [editingBudgetLotId, setEditingBudgetLotId] = useState<string | null>(null);
  const [draftBudget, setDraftBudget] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const budgetInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingLotId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingLotId]);

  useEffect(() => {
    if (editingBudgetLotId && budgetInputRef.current) {
      budgetInputRef.current.focus();
      budgetInputRef.current.select();
    }
  }, [editingBudgetLotId]);

  const startEdit = (lot: Block) => {
    setEditingLotId(lot.id);
    setDraftName(lot.content.titre || '');
  };

  const commitEdit = () => {
    if (!editingLotId) return;
    const trimmed = draftName.trim();
    if (trimmed.length > 0) onRenameLot(editingLotId, trimmed);
    setEditingLotId(null);
  };

  const cancelEdit = () => {
    setEditingLotId(null);
    setDraftName('');
  };

  const startEditBudget = (lot: Block) => {
    setEditingBudgetLotId(lot.id);
    setDraftBudget(lot.budgetInitial && lot.budgetInitial > 0 ? String(Math.round(lot.budgetInitial)) : '');
  };

  const commitEditBudget = () => {
    if (!editingBudgetLotId || !onUpdateBudgetInitial) {
      setEditingBudgetLotId(null);
      return;
    }
    const trimmed = draftBudget.trim().replace(/\s/g, '').replace(',', '.');
    if (trimmed === '') {
      onUpdateBudgetInitial(editingBudgetLotId, undefined);
    } else {
      const n = Number(trimmed);
      if (!isNaN(n) && n >= 0) {
        onUpdateBudgetInitial(editingBudgetLotId, n > 0 ? n : undefined);
      }
    }
    setEditingBudgetLotId(null);
  };

  const cancelEditBudget = () => {
    setEditingBudgetLotId(null);
    setDraftBudget('');
  };

  const handleDelete = (lot: Block) => {
    const ok = window.confirm(
      `Supprimer le lot « ${lot.content.titre || 'Sans titre'} » et toutes ses prestations ?`
    );
    if (!ok) return;
    onDeleteLot(lot.id);
    setEditingLotId(null);
  };

  const tabClass = (isActive: boolean) =>
    `group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all select-none whitespace-nowrap ${
      isActive
        ? 'bg-white border border-violet-300 text-violet-700 shadow-sm'
        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-transparent'
    }`;

  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1">
      <button
        type="button"
        onClick={onAddLot}
        title="Ajouter un lot"
        className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-violet-700 hover:bg-violet-50 border border-dashed border-gray-300 hover:border-violet-300 transition-colors flex-shrink-0"
      >
        <PlusIcon className="w-4 h-4" />
      </button>
      {showRecap && (
        <button
          type="button"
          onClick={() => onSelectLot(null)}
          className={tabClass(activeLotId === null)}
          title="Vue récap — somme de tous les lots"
        >
          <Squares2X2Icon className="w-4 h-4" />
          <span>Récap</span>
        </button>
      )}
      {lots.map(lot => {
        const isEditing = editingLotId === lot.id;
        const isEditingBudget = editingBudgetLotId === lot.id;
        const isActive = activeLotId === lot.id;
        const budget = lotBudgets?.get(lot.id);

        if (isEditing) {
          return (
            <div
              key={lot.id}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-violet-400 shadow-sm"
            >
              <input
                ref={inputRef}
                type="text"
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitEdit();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEdit();
                  }
                }}
                className="text-sm font-medium text-violet-700 bg-transparent outline-none min-w-[120px] max-w-[260px]"
              />
              <button
                type="button"
                // onMouseDown au lieu d'onClick : évite le blur de l'input qui commit avant le delete
                onMouseDown={e => {
                  e.preventDefault();
                  handleDelete(lot);
                }}
                title="Supprimer ce lot"
                className="flex items-center justify-center w-6 h-6 rounded text-red-500 hover:bg-red-50"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          );
        }

        if (isEditingBudget) {
          return (
            <div
              key={lot.id}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-violet-400 shadow-sm"
            >
              <span className="text-sm font-medium text-violet-700 truncate max-w-[160px]">
                {lot.content.titre || 'Sans titre'}
              </span>
              <span className="text-xs text-gray-400 ml-1">Initial</span>
              <input
                ref={budgetInputRef}
                type="text"
                inputMode="numeric"
                value={draftBudget}
                onChange={e => setDraftBudget(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitEditBudget();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEditBudget();
                  }
                }}
                placeholder="—"
                className="text-sm font-medium text-violet-700 bg-transparent outline-none w-20 text-right tabular-nums border-b border-violet-200 focus:border-violet-500"
              />
              <span className="text-xs text-gray-500">€</span>
              <button
                type="button"
                onMouseDown={e => {
                  e.preventDefault();
                  commitEditBudget();
                }}
                title="Valider"
                className="flex items-center justify-center w-6 h-6 rounded text-violet-600 hover:bg-violet-50"
              >
                <CheckIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onMouseDown={e => {
                  e.preventDefault();
                  cancelEditBudget();
                }}
                title="Annuler"
                className="flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:bg-gray-100"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          );
        }

        const tooltip = budget?.hasInitial
          ? `Initial ${BlockCalculationService.formatPrice(budget.initial as number)} € → Actuel ${BlockCalculationService.formatPrice(budget.current)} € (${formatDeltaPct(budget.deltaPct)}, ${formatDeltaAmount(budget.delta)}) — double-clic pour renommer`
          : `${lot.content.titre || 'Sans titre'} — double-clic pour renommer`;

        return (
          <div key={lot.id} className="relative group flex items-center">
            <button
              type="button"
              onClick={() => onSelectLot(lot.id)}
              onDoubleClick={() => startEdit(lot)}
              className={tabClass(isActive)}
              title={tooltip}
            >
              <span className="truncate max-w-[200px]">{lot.content.titre || 'Sans titre'}</span>
              {budget && budget.current > 0 && (
                <span className="text-xs text-gray-400 tabular-nums font-normal">
                  {BlockCalculationService.formatPrice(budget.current)} €
                </span>
              )}
              {budget?.hasInitial && (
                <span className={`text-xs tabular-nums font-medium ${severityTextClass(budget.severity)}`}>
                  {formatDeltaPct(budget.deltaPct)}
                </span>
              )}
            </button>
            {onUpdateBudgetInitial && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  startEditBudget(lot);
                }}
                title={budget?.hasInitial ? 'Modifier le budget initial' : 'Définir un budget initial'}
                className="ml-0.5 flex items-center justify-center w-5 h-5 rounded text-gray-300 hover:text-violet-600 hover:bg-violet-50 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <PencilSquareIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LotTabs;
